import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ScrollAnimation from '../../components/base/ScrollAnimation';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useSEO, generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function WindowTintingPage() {
  const tintTypes = [
    {
      name: 'Ceramic Tint',
      price: 'Premium',
      image: 'https://img1.wsimg.com/isteam/ip/abffa065-2f17-4bc9-b67a-6803e73e4f84/03-0004.png',
      features: [
        'Superior heat rejection up to 80%',
        'Blocks 99% of harmful UV rays',
        'No signal interference',
        'Non-metallic construction',
        'Lifetime warranty included',
        'Color stable - won\'t fade',
      ],
      benefits: 'Best performance and durability. Perfect for luxury vehicles and maximum comfort.',
    },
    {
      name: 'Standard Tint',
      price: 'Budget-Friendly',
      image: 'https://img1.wsimg.com/isteam/ip/abffa065-2f17-4bc9-b67a-6803e73e4f84/01-f2adb302.png',
      features: [
        'Enhanced privacy',
        'Reduces glare effectively',
        'Sleek dark appearance',
        'Most affordable option',
        'Good UV protection',
        'Professional installation',
      ],
      benefits: 'Perfect for privacy and aesthetics on a budget. Great entry-level tinting solution.',
    },
  ];

  const faqs = [
    {
      question: 'How long does window tint installation take?',
      answer: 'Most vehicles can be completed in 2-4 hours. We offer same-day service and you can wait in our comfortable lounge or we can arrange pickup/delivery.',
    },
    {
      question: 'What is the warranty on window tinting?',
      answer: 'We offer a lifetime warranty on our ceramic tint, covering bubbling, peeling, fading, and discoloration. Standard tint comes with a 3-year warranty.',
    },
    {
      question: 'Is window tinting legal in Maryland?',
      answer: 'Yes! Maryland law allows 35% VLT on front side windows and any darkness on rear windows. We ensure all installations meet state regulations.',
    },
    {
      question: 'How do I care for my new window tint?',
      answer: 'Wait 3-5 days before rolling windows down. Clean with ammonia-free cleaner and soft cloth. Avoid abrasive materials. Your tint will last for years with proper care.',
    },
    {
      question: 'Can you tint already tinted windows?',
      answer: 'We can remove old tint and install new film. If factory tint is present, we can add additional layers while staying within legal limits.',
    },
    {
      question: 'Do you offer mobile tinting services?',
      answer: 'Yes! We bring our professional equipment to your location. Perfect for busy schedules or fleet services. Additional travel fee may apply.',
    },
  ];

  useSEO({
    title: 'Window Tinting Baltimore - Ceramic & Standard Tint | U.B.K Window Tint',
    description: 'Professional window tinting in Baltimore, MD. Choose ceramic or standard tint with lifetime warranty. Superior heat rejection, UV protection, and privacy. Same-day service available. Call (443) 800-0348.',
    keywords: 'window tinting Baltimore, ceramic tint Maryland, car window tint, UV protection, heat rejection, lifetime warranty',
    canonical: '/window-tinting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateServiceSchema(
          'Window Tinting',
          'Professional window tinting services with ceramic and standard options. Lifetime warranty, superior heat rejection, and UV protection.',
          '/window-tinting'
        ),
        generateFAQSchema(faqs),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Window Tinting', url: '/window-tinting' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-24 max-md:pt-20">
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-12 bg-gradient-to-br from-secondary via-gray-800 to-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(45deg, transparent 48%, white 49%, white 51%, transparent 52%)',
              backgroundSize: '20px 20px',
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
              <div className="text-white">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight">
                  Professional Window Tinting Services
                </h1>
                <p className="text-sm md:text-xl text-gray-300 mb-4 md:mb-8 leading-relaxed">
                  Transform your vehicle with premium window tinting. Choose from ceramic or standard options for superior protection, comfort, and style.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
                  <Link to="/contact">
                    <Button size="lg" className="text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-calendar-line mr-1 md:mr-2"></i>
                      Request Appointment
                    </Button>
                  </Link>
                  <a href="tel:4438000348">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-secondary text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-phone-line mr-1 md:mr-2"></i>
                      Get Free Quote
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://img1.wsimg.com/isteam/ip/9ebfc883-4205-41ee-a091-a5f3a9011f92/blob-0005.png"
                  alt="Professional Window Tinting"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tint Types Section */}
        <section className="py-8 md:py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Choose Your Tint Type
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  We use premium Flex Film products for all installations. Each type offers unique benefits to match your needs and budget.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
              {tintTypes.map((tint, index) => (
                <ScrollAnimation key={tint.name} animation="slide-up" delay={index * 100}>
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                    <div className="relative h-40 md:h-64">
                      <img
                        src={tint.image}
                        alt={tint.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 md:top-4 right-2 md:right-4 bg-primary text-white px-2 md:px-4 py-1 md:py-2 rounded-full font-bold text-xs md:text-base">
                        {tint.price}
                      </div>
                    </div>
                    
                    <div className="p-4 md:p-6 flex-1 flex flex-col">
                      <h3 className="text-lg md:text-2xl font-bold text-secondary mb-2 md:mb-4">{tint.name}</h3>
                      <p className="text-xs md:text-base text-gray-600 mb-3 md:mb-6 leading-relaxed">{tint.benefits}</p>
                      
                      <div className="space-y-1.5 md:space-y-3 mb-4 md:mb-6 flex-1">
                        {tint.features.map((feature) => (
                          <div key={feature} className="flex items-start space-x-2 md:space-x-3">
                            <i className="ri-check-line text-primary text-base md:text-xl mt-0.5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0"></i>
                            <span className="text-xs md:text-base text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Link to="/contact">
                        <Button fullWidth className="text-xs md:text-base py-2 md:py-3">Get Quote</Button>
                      </Link>
                    </div>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Our Installation Process
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Professional installation ensures perfect results that last. Here's what to expect.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {[
                {
                  step: '01',
                  title: 'Consultation',
                  description: 'We discuss your needs, show samples, and help you choose the perfect tint shade and type.',
                  icon: 'ri-chat-check-line',
                },
                {
                  step: '02',
                  title: 'Preparation',
                  description: 'Windows are thoroughly cleaned and prepped. We ensure a dust-free environment for perfect adhesion.',
                  icon: 'ri-brush-line',
                },
                {
                  step: '03',
                  title: 'Installation',
                  description: 'Expert technicians apply film with precision. We use professional tools for bubble-free results.',
                  icon: 'ri-tools-line',
                },
                {
                  step: '04',
                  title: 'Quality Check',
                  description: 'Final inspection ensures perfect installation. We provide care instructions and warranty information.',
                  icon: 'ri-shield-check-line',
                },
              ].map((process, index) => (
                <ScrollAnimation key={process.step} animation="slide-up" delay={index * 100}>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                      <i className={`${process.icon} text-white text-xl md:text-3xl`}></i>
                    </div>
                    <div className="text-2xl md:text-4xl font-bold text-gray-200 mb-1 md:mb-2">{process.step}</div>
                    <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{process.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{process.description}</p>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-sm md:text-xl text-gray-600">
                  Everything you need to know about window tinting
                </p>
              </div>
            </ScrollAnimation>

            <div className="space-y-2 md:space-y-4">
              {faqs.map((faq, index) => (
                <ScrollAnimation key={index} animation="slide-up" delay={index * 50}>
                  <details className="bg-white rounded-xl shadow-md overflow-hidden group">
                    <summary className="px-4 md:px-6 py-3 md:py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm md:text-lg font-semibold text-secondary pr-4">{faq.question}</h3>
                      <i className="ri-arrow-down-s-line text-xl md:text-2xl text-primary group-open:rotate-180 transition-transform w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-6 pb-3 md:pb-5 text-xs md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-24 bg-gradient-to-br from-primary to-accent text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollAnimation animation="fade-in">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6">
                Ready to Upgrade Your Vehicle?
              </h2>
              <p className="text-sm md:text-xl mb-4 md:mb-8 opacity-90">
                Get professional window tinting with lifetime warranty. Book your appointment today!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="min-w-[160px] md:min-w-[200px] text-xs md:text-base py-2 md:py-3">
                    <i className="ri-calendar-line mr-1 md:mr-2"></i>
                    Book Now
                  </Button>
                </Link>
                <a href="tel:4438000348">
                  <Button size="lg" variant="outline" className="min-w-[160px] md:min-w-[200px] border-white text-white hover:bg-white hover:text-primary text-xs md:text-base py-2 md:py-3">
                    <i className="ri-phone-line mr-1 md:mr-2"></i>
                    (443) 800-0348
                  </Button>
                </a>
              </div>
            </ScrollAnimation>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
